import {apiClient} from './client'

export type WorkflowSchedule = 'daily' | 'weekly' | 'monthly' | 'manual'
export type RegionTier = 1 | 2 | 3
export type HealthStatus =
	| 'healthy'
	| 'quiet'
	| 'stale'
	| 'failed'
	| 'running'
	| 'unknown'

export type WorkflowRunSummary = {
	id: string
	status: string
	startedAt: string
	completedAt: string | null
	durationMs: number | null
	metadata: Record<string, unknown> | null
	errorMessage: string | null
	githubRunUrl: string | null
	artifactCount: number
}

export type RunArtifactContentType = 'PDF' | 'HTML' | 'JSON'

export type RunArtifact = {
	id: string
	contentType: RunArtifactContentType
	sourceUrl: string | null
	fetchDate: string
	externalPath: string | null
	sizeBytes: number | null
	permitCount: number
	linkSource: 'fk' | 'heuristic'
}

export type WorkflowSummary = {
	workflow: string
	schedule: WorkflowSchedule
	/** Green-sheet value tier (1 = must be all green); null = operational job */
	tier: RegionTier | null
	/** Green-sheet rank for within-tier display order; null = unranked */
	rank: number | null
	health: HealthStatus
	/** Set when the workflow must be run by hand (not dispatchable from the UI) */
	manual: {reason: string; localCommand: string | null} | null
	lastRun: {
		startedAt: string
		status: string
		durationMs: number | null
		metadata: Record<string, unknown> | null
	} | null
	lastSuccessAt: string | null
	nextRunAt: string | null
	runs: WorkflowRunSummary[]
}

export type DashboardData = {
	workflows: WorkflowSummary[]
}

export type HealthSummary = {
	healthy: number
	quiet: number
	stale: number
	failed: number
	running: number
	unknown: number
	issues: Array<{
		workflow: string
		health: HealthStatus
		lastRunAt: string | null
	}>
}

export async function getWorkflowsDashboard(
	historyCount = 90,
): Promise<{data: DashboardData}> {
	return apiClient<{data: DashboardData}>(
		`/api/workflows/dashboard?historyCount=${historyCount}`,
	)
}

export async function getWorkflowsHealth(): Promise<{data: HealthSummary}> {
	return apiClient<{data: HealthSummary}>('/api/workflows/health')
}

export async function getWorkflowRuns(
	workflow: string,
	limit = 50,
	offset = 0,
): Promise<{data: {runs: WorkflowRunSummary[]; total: number}}> {
	return apiClient<{data: {runs: WorkflowRunSummary[]; total: number}}>(
		`/api/workflows/${workflow}/runs?limit=${limit}&offset=${offset}`,
	)
}

export async function triggerWorkflowDispatch(workflow: string): Promise<void> {
	await apiClient(`/api/workflows/${workflow}/trigger`, {
		method: 'POST',
		responseType: 'text',
	})
}

export type RegionIntake = {
	region: string
	count: number
}

export type PeriodIntake = {
	since: string
	total: number
	regions: RegionIntake[]
}

export type PermitIntakeData = {
	periods: {
		day: PeriodIntake
		week: PeriodIntake
		month: PeriodIntake
	}
}

export async function getPermitIntake(): Promise<{data: PermitIntakeData}> {
	return apiClient<{data: PermitIntakeData}>('/api/workflows/permit-intake')
}

export type IntakeBucket = 'issued' | 'ingested'

export type DailyIntakeRow = {
	date: string
	region: string
	count: number
}

export type PermitIntakeDailyData = {
	year: number
	month: number
	bucket: IntakeBucket
	monthStart: string
	monthEnd: string
	rows: DailyIntakeRow[]
	regions: string[]
}

export async function getPermitIntakeDaily(
	year: number,
	month: number,
	bucket: IntakeBucket,
): Promise<{data: PermitIntakeDailyData}> {
	return apiClient<{data: PermitIntakeDailyData}>(
		`/api/workflows/permit-intake/daily?year=${year}&month=${month}&bucket=${bucket}`,
	)
}

export type InfrastructureBackup = {
	filename: string
	gcsPath: string
	sizeBytes: number
	uploadedAt: string
}

export type InfrastructureBackupsData = {
	backups: InfrastructureBackup[]
	disabled: boolean
	reason?: string
}

export async function getInfrastructureBackups(): Promise<{
	data: InfrastructureBackupsData
}> {
	return apiClient<{data: InfrastructureBackupsData}>(
		'/api/infrastructure/backups',
	)
}

export async function getWorkflowRunArtifacts(
	runId: string,
): Promise<{data: {artifacts: RunArtifact[]}}> {
	return apiClient<{data: {artifacts: RunArtifact[]}}>(
		`/api/workflow-runs/${runId}/artifacts`,
	)
}

export async function getRawSourceContent(rawSourceId: string): Promise<Blob> {
	return apiClient<Blob>(`/api/raw-sources/${rawSourceId}/content`, {
		responseType: 'blob',
	})
}
